import './CreateReply.scss';
import clsx from 'clsx';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import ClickAwayListener from '@/src/components/common/ClickAwayListener/ClickAwayListener';
import useDiscardChanges from '@/src/components/common/DiscardChanges/useDiscardChanges';
import ModalPopup from '@/src/components/common/Elements/Modal/ModalPopup/ModalPopup';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import NavigationBlocker from '@/src/components/common/NavigationBlocker/NavigationBlocker';
import ComposeEmailForm from '@/src/components/project/Inbox2/components/ComposeEmailForm/ComposeEmailForm';
import PreviousMessage from '@/src/components/project/Inbox2/components/EmailEditor/PreviousMessage';
import { useUserEmailSignature } from '@/src/components/project/Inbox2/components/EmailEditor/useEmailSignature';
import { useFilteredEmailRecipients } from '@/src/components/project/Inbox2/components/EmailEditor/useFilteredEmailRecipients';
import { ELLIPSIS_CLASS } from '@/src/components/project/Inbox2/Config/inbox.config';
import { getUserWithFullNameAndEmaiDisplayValue } from '@/src/components/project/Inbox2/models/EmailEntry';
import { EmailEntry, ReplyType } from '@/src/components/project/Inbox2/types';
import { showToast } from '@/src/globalFunction';
import useDidMountEffect from '@/src/hooks/useDidMountEffect';

import HeaderActions from './HeaderActions/HeaderActions';

import { useCheckReplyEmail } from '../../../api/checkReplyEmail';
import { useCreateReply } from '../../../api/createReply';
import { useEmailThreadEntryViewer } from '../../../store/emailThreadEntryViewer.store';

type Recipient = {
  label: string;
  value: string;
  valid: boolean;
  bounce?: boolean;
};

const mapRecipientTag = (recipient, valid = true) => {
  return {
    label: recipient.fullName || recipient.emailAddress,
    value: recipient.emailAddress,
    valid: valid,
    bounce: false,
  };
};

const mapRecipientsTag = (recipients) => {
  return recipients.map((recipient) => mapRecipientTag(recipient));
};

const getToRecipients = (emailEntry, replyType) => {
  const recipients: { [key: string]: Recipient } = {};

  mapRecipientsTag(emailEntry.from).forEach((item) => (recipients[item.value] = item));

  if (replyType === ReplyType.REPLY_ALL) {
    mapRecipientsTag(emailEntry.to).forEach((item) => (recipients[item.value] = item));
  }

  return Object.values(recipients);
};

const getCcRecipients = (emailEntry, replyType) => {
  const recipients: { [key: string]: Recipient } = {};

  if (replyType === ReplyType.REPLY_ALL) {
    mapRecipientsTag(emailEntry.cc).forEach((item) => (recipients[item.value] = item));
  }

  return Object.values(recipients);
};

const getToRecipientWithBounced = (emailEntry, replyType, recipientsOptions) => {
  const recipients = getToRecipients(emailEntry, replyType);

  return recipients.map((recipient) => {
    const ro = recipientsOptions.find((ro) => ro.value === recipient.value);
    if (ro) {
      return {
        ...recipient,
        bounce: ro.bounce,
      };
    }
    return recipient;
  });
};

const getCcRecipientsWithBounced = (emailEntry, replyType, recipientsOptions) => {
  const recipients = getCcRecipients(emailEntry, replyType);

  return recipients.map((recipient) => {
    const ro = recipientsOptions.find((ro) => ro.value === recipient.value);
    if (ro) {
      return {
        ...recipient,
        bounce: ro.bounce,
      };
    }
    return recipient;
  });
};

const INITIAL_UPDATE_EMAIL_OPTIONS = {
  email: '',
  showModal: false,
  notified: false,
};

type CreateReplyProps = {
  projectId: string;
  emailEntry: EmailEntry;
};

const CreateReply = ({ projectId, emailEntry }: CreateReplyProps) => {
  const containerRef = useRef(null);
  const { replyType, setReplyType } = useEmailThreadEntryViewer();
  const { recipientsOptions } = useFilteredEmailRecipients({
    projectId,
  });
  const emailSignature = useUserEmailSignature();
  const [toRecipients, setToRecipients] = useState(
    getToRecipientWithBounced(emailEntry, replyType, recipientsOptions)
  );
  const [ccRecipients, setCcRecipients] = useState(
    getCcRecipientsWithBounced(emailEntry, replyType, recipientsOptions)
  );
  const [files, setFiles] = useState<[]>([]);
  const [hasInput, setHasInput] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [updatedEmailOptions, setUpdatedEmailOptions] = useState({
    ...INITIAL_UPDATE_EMAIL_OPTIONS,
  });
  const createReplyMutation = useCreateReply({
    projectId,
    inboxEntryId: emailEntry.inboxEntryId,
  });
  const checkReplyEmailMutation = useCheckReplyEmail({
    projectId,
    threadId: emailEntry.inboxEntryId,
    emails: getToRecipients(emailEntry, replyType),
  });
  const [DiscardChangesModal, confirmDiscard] = useDiscardChanges();
  const isLoading = checkReplyEmailMutation.isLoading || createReplyMutation.isLoading;

  const hasRecipientsChanged = useMemo(() => {
    if (!replyType) {
      return false;
    }

    const hasValidTo = toRecipients.some((r) => r.valid);
    const hasValidCc = ccRecipients.some((r) => r.valid);

    return (
      (hasValidTo && !_.isEqual(toRecipients, getToRecipients(emailEntry, replyType))) ||
      (hasValidCc && !_.isEqual(ccRecipients, getCcRecipients(emailEntry, replyType)))
    );
  }, [toRecipients, ccRecipients, emailEntry, replyType]);

  const hasToRecipients = useMemo(
    () => toRecipients.findIndex((r) => r.valid && !r.bounce) >= 0,
    [toRecipients]
  );
  const hasValidToRecipients = useMemo(
    () => (toRecipients.length > 0 ? toRecipients.findIndex((r) => r.bounce) < 0 : true),
    [toRecipients]
  );
  const hasValidCcRecipients = useMemo(
    () => (ccRecipients.length > 0 ? ccRecipients.findIndex((r) => r.bounce) < 0 : true),
    [ccRecipients]
  );
  const hasFiles = files.length > 0;
  const hasChanges = hasInput || hasRecipientsChanged || hasFiles;

  const isSubmitEnabled =
    (hasInput || hasFiles) &&
    hasToRecipients &&
    hasValidCcRecipients &&
    hasValidToRecipients &&
    !isUploading;

  useDidMountEffect(() => {
    setToRecipients(getToRecipientWithBounced(emailEntry, replyType, recipientsOptions));
    setCcRecipients(getCcRecipientsWithBounced(emailEntry, replyType, recipientsOptions));
  }, [replyType, emailEntry, recipientsOptions]);

  useEffect(() => {
    // Editor is hidden, reset
    if (!replyType) {
      setToRecipients(getToRecipientWithBounced(emailEntry, replyType, recipientsOptions));
      setCcRecipients(getCcRecipientsWithBounced(emailEntry, replyType, recipientsOptions));
      setReplyType(null);
      setHasInput(null);
      setIsUploading(false);
      setFiles([]);
      setUpdatedEmailOptions({ ...INITIAL_UPDATE_EMAIL_OPTIONS });
    }
  }, [replyType, setReplyType, emailEntry, recipientsOptions]);

  useEffect(() => {
    if (!replyType) {
      return;
    }

    if (!checkReplyEmailMutation.data) {
      return;
    }

    if (!checkReplyEmailMutation.data.hasChanges) {
      return;
    }

    setUpdatedEmailOptions({
      showModal: true,
      email: checkReplyEmailMutation.data.newEmail,
      notified: true,
    });
    setToRecipients(checkReplyEmailMutation.data.emails);
  }, [checkReplyEmailMutation.data, emailEntry, projectId, replyType]);

  const handleClickReply = useCallback(
    async (replyType) => {
      setReplyType(replyType);
    },
    [setReplyType]
  );

  const handleSend = useCallback(
    async ({ messageHTML, rawMessage, currentInlineImages }) => {
      const to = toRecipients.filter((r) => r.valid).map((r) => r.value);
      const cc = ccRecipients.filter((r) => r.valid).map((r) => r.value);

      const view = ReactDOMServer.renderToString(<PreviousMessage previousMessage={emailEntry} />);

      const replyMessageHTML = `${messageHTML}<div class="${ELLIPSIS_CLASS}">${view}</div>`;

      const params = {
        to,
        cc,
        html: replyMessageHTML,
        raw: rawMessage,
        files: [...files, ...currentInlineImages],
        projectId,
        'email-id': emailEntry.id,
      };

      const response = await createReplyMutation.mutateAsync({
        inboxEntryId: emailEntry.inboxEntryId,
        params,
      });

      if (response) {
        showToast('success', 'Reply successfully sent!');
        setReplyType(null);
        // syncInboxEntry(response.data['inbox-entry']);
      }
    },
    [projectId, emailEntry, ccRecipients, createReplyMutation, files, toRecipients, setReplyType]
  );

  const closeUpdatedEmailModal = useCallback(() => {
    setUpdatedEmailOptions({
      ...updatedEmailOptions,
      showModal: false,
    });
  }, [updatedEmailOptions]);

  const handleCancel = () => {
    if (hasChanges) {
      handleDiscard();
    } else {
      setReplyType(null);
    }
  };

  const handleDiscard = async () => {
    if (await confirmDiscard()) {
      showToast('success', 'Changes have been discarded!');
      setReplyType(null);
    }
  };

  const handleClickOutside = async (event) => {
    let isTargetAllowed = false;

    if (event.target.closest('.tox')) {
      isTargetAllowed = true;
    }

    if (containerRef && !containerRef.current.contains(event.target) && !isTargetAllowed) {
      if (hasChanges) {
        event.preventDefault();
        event.stopPropagation();

        handleDiscard();
      }
    }
  };

  const handleUpload = (files) => {
    setFiles(files);
    containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
  };

  useEffect(() => {
    if (!replyType) {
      return;
    }

    containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
  }, [replyType]);
  const fromText = emailEntry.from
    .map((from) => getUserWithFullNameAndEmaiDisplayValue(from))
    .join(',');

  return (
    <ClickAwayListener onClickAway={handleClickOutside}>
      <div className="email-editor--container">
        <div
          className={clsx('container--createReply', 'tw-mx-[10px] tw-mt-[20px] tw-mb-[10px]')}
          style={{ minHeight: '460px' }}
          ref={containerRef}
        >
          {isLoading && <Spinner />}
          <HeaderActions
            replyingTo={fromText}
            onClickReply={handleClickReply}
            onCancel={handleCancel}
            replyType={replyType}
          />
          <div
            className={clsx(
              'container--replyBox',
              'tw-border-solid tw-border-[1px] tw-border-cfo-blue-300',
              'tw-shadow-[0px_0px_2px_0px_rgba(65,74,79,0.30)]',
              'tw-rounded-[5px]',
              'tw-mt-[20px]'
            )}
          >
            <ComposeEmailForm
              isReply={true}
              isSubmitEnabled={isSubmitEnabled}
              projectId={projectId}
              previousMessage={emailEntry}
              emailSignature={emailSignature}
              toRecipients={toRecipients}
              ccRecipients={ccRecipients}
              files={files}
              isUploading
              onUpload={handleUpload}
              setToRecipients={setToRecipients}
              setCcRecipients={setCcRecipients}
              onSend={handleSend}
              setIsUploading={setIsUploading}
              setHasInput={setHasInput}
              uploaderId={'reply'}
              editorHeight={'256px'}
            />
          </div>

          <NavigationBlocker navigationBlocked={hasChanges} />
          <DiscardChangesModal />
          {updatedEmailOptions.showModal && (
            <ModalPopup
              title="Account email updated"
              message={`Email ID of this Account has been updated to ${updatedEmailOptions.email}`}
              modalWidth={clsx('tw-w-[520px]')}
              onConfirm={closeUpdatedEmailModal}
              onClose={closeUpdatedEmailModal}
            />
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
};

export default CreateReply;
